// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import { dollars } from 'Utils/numberFormat';

// The pole line beside a pole product's per-foot rate (the Lovable delta §16 step A, Joel 2026-10-22): the poles the
// project's length holds at the density's pole spacing, both from the quote, and the price per pole. The price per pole
// is the per-foot rate as the page prints it (four places) times the spacing, to the cent, so $/ft × spacing reads true
// beside it. aw-server's catalog_pricing.pricePerPoleDollar applies the same rule to the order PDF; the two share their
// test vectors. Display only: the price is still the corridor's per-foot rate.

export interface IPoleEstimate {
  estimatedPoles: number;
  pricePerPoleDollar: number;
}

export const pricePerPoleDollar = (pricePerFoot: number, poleSpacingFt: number): number | null => {
  if (!Number.isFinite(pricePerFoot) || !(poleSpacingFt > 0)) return null;
  const printedPerFoot = Math.round(pricePerFoot * 10000) / 10000;
  return Math.round(printedPerFoot * poleSpacingFt * 100) / 100;
};

// The pole line for a quote carrying a pole estimate and a priced per-foot rate; null otherwise
export const poleEstimateFor = (poleSpacingFt: number | undefined, estimatedPoles: number | undefined, pricePerFoot: number): IPoleEstimate | null => {
  if (!poleSpacingFt || !estimatedPoles || !(pricePerFoot > 0)) return null;
  const perPole = pricePerPoleDollar(pricePerFoot, poleSpacingFt);
  return perPole === null ? null : { estimatedPoles, pricePerPoleDollar: perPole };
};

// ≈ 167 poles · $85.35 per pole (the count is a count, printed as it is)
export const poleLineText = ({ estimatedPoles, pricePerPoleDollar: perPole }: IPoleEstimate): string => (
  `≈ ${estimatedPoles.toLocaleString('en-US')} ${estimatedPoles === 1 ? 'pole' : 'poles'} · ${dollars(perPole)} per pole`
);
